import React, { useEffect, useMemo, useState } from "react";
// TODO extract these from Dashboard
import {
  Content,
  Flex,
  FullPageContainer,
  Right,
  Statuses,
  TileContainer,
} from "../Dashboard/Dashboard";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { styled } from "styled-components";
import { ChevronRight } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { GreenCircle, RedCircle } from "../Vehicles/Vehicles";
import Map, { MapVehicle } from "../../components/Map/Map";
import TldPng from "../../assets/tld.png";
import {
  displayPosition,
  localizationStdM,
  useTractors,
} from "../../contexts/TractorContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import { LatLng, LatLngExpression } from "leaflet";
import MultiViewTable, {
  View,
} from "../../components/MultiViewTable/MultiViewTable";
import {
  getLifetimeMetrics,
  getThisWeekMetrics,
  getTodayMetrics,
} from "./metricViews";
import { atlMapCenter } from "../../utils/constants";
import GreyedOutMap from "../../components/GreyedOutMap/GreyedOutMap";

const VehicleDetail = () => {
  const navigate = useNavigate();
  const { tractors } = useTractors();
  const { hasPermission } = usePermissions();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // This is the display name
  const { vehicleName } = useParams();

  // Find the tractor object with the matching displayName
  const tractor = useMemo(() => {
    for (let value of tractors.values()) {
      if (value.displayName === vehicleName) {
        return value;
      }
    }
    return null; // Return null if no matching tractor is found
  }, [tractors, vehicleName]);

  // This is the actual name, or ID. av4 for example
  const tractorID = useMemo(() => {
    for (let [id, value] of tractors.entries()) {
      if (value.displayName === vehicleName) {
        return id;
      }
    }
    return null; // Return null if no matching tractor is found
  }, [tractors, vehicleName]);

  const tractorPosition: LatLngExpression = useMemo(() => {
    // If there's no tractor or no known position, return a default position.
    // This won't matter since we won't render the map in this case anyway.
    const position = tractor ? displayPosition(tractor) : null;
    if (!position) {
      return new LatLng(0, 0);
    }
    return new LatLng(position.lat, position.lon);
  }, [tractor]);

  const mapVehicle: MapVehicle[] = useMemo(() => {
    // If there's no tractor, return an empty array
    if (!tractor) {
      return [];
    }
    const status: MapVehicle["status"] = tractor.statuses.online
      ? "online"
      : "offline";
    return [
      {
        id: tractor.displayName,
        position: tractorPosition,
        label: tractor.displayName,
        action: () => {},
        status,
        confidenceRadiusM: localizationStdM(tractor),
      },
    ];
  }, [tractor, tractorPosition]);

  const metricViews: View[] = [
    {
      name: "Today",
      dataSource: getTodayMetrics,
      params: [tractorID],
    },
    {
      name: "This Week",
      dataSource: getThisWeekMetrics,
      params: [tractorID],
    },
    {
      name: "All Time",
      dataSource: getLifetimeMetrics,
      params: [tractorID],
    },
  ];

  // Resolve once so the layout branch is stable across renders. Matches the
  // pattern used in Dashboard.tsx for the same permission. Gates both the
  // metrics tile and the Last Message row (the backend also strips
  // last_seen_at for non-AeroVect callers).
  const isAerovectUser = hasPermission("read:aerovect-vehicles");

  const lastMessageAt = useMemo(() => {
    if (!tractor || !tractor.statuses.lastSeenAt) {
      return null;
    }
    return new Date(tractor.statuses.lastSeenAt).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  }, [tractor]);

  return (
    <FullPageContainer>
      <Navbar />
      <Right>
        {tractor && (
          <>
            <PageHeaderContainer>
              <Breadcrumb onClick={() => navigate("/vehicles")}>
                Vehicles
              </Breadcrumb>
              <Chevron />
              <span
                style={{
                  paddingRight: 10,
                  textAlign: "center",
                }}
              >
                {vehicleName}
              </span>
              {tractor.statuses.online ? <GreenCircle /> : <RedCircle />}
            </PageHeaderContainer>
            <Content style={{ overflow: "auto" }}>
              <TileContainer>
                {/* $standalone drops the bottom divider (no sibling inside this
                    TileContainer). $grow makes the Flex fill the TileContainer's
                    vertical space — necessary because Content is flex-wrap with
                    default align-content: stretch, so each wrapped row (and the
                    TileContainer inside it) stretches to fill Content's height.
                    Without $grow, the Flex sits at the top and leaves a visible
                    empty area inside the TileContainer's border (the "ghost"). */}
                <Flex $standalone $grow>
                  <Statuses style={{ minWidth: 250 }}>
                    <StatusHeader>
                      {tractor.statuses.online ? (
                        <GreenCircle />
                      ) : (
                        <RedCircle />
                      )}
                      {vehicleName}
                    </StatusHeader>
                    <StatusRow>
                      <StatusName>Status</StatusName>
                      <StatusValue>
                        {tractor.statuses.online ? "Online" : "Offline"}
                      </StatusValue>
                    </StatusRow>
                    {tractor.statuses.online && (
                      <>
                        <StatusRow>
                          <StatusName>Destination</StatusName>
                          <StatusValue>
                            {tractor.statuses.destination || "None"}
                          </StatusValue>
                        </StatusRow>
                        {/* <StatusRow>
													<StatusName>
														Requested Ride
													</StatusName>
													<StatusValue>
														{tractor.statuses
															.requestedRide ||
															'None'}
													</StatusValue>
												</StatusRow> */}
                      </>
                    )}
                    {isAerovectUser && (
                      <StatusRow>
                        <StatusName>Last Message</StatusName>
                        <StatusValue>{lastMessageAt || "Never"}</StatusValue>
                      </StatusRow>
                    )}

                    <TldPngContainer>
                      <BigTldPng src={TldPng} alt="TLD" />
                    </TldPngContainer>
                  </Statuses>
                  {displayPosition(tractor) ? (
                    <Map
                      center={tractorPosition}
                      vehicles={mapVehicle}
                      isLastKnownPosition={!tractor.statuses.online}
                      shouldDisplayRecenterOnTractorButton
                      height={
                        isSmallScreen
                          ? Math.round(window.innerHeight * 0.4)
                          : undefined
                      }
                    />
                  ) : (
                    <GreyedOutMap
                      center={atlMapCenter}
                      height={
                        isSmallScreen
                          ? Math.round(window.innerHeight * 0.4)
                          : undefined
                      }
                    >
                      Could not find last known position
                    </GreyedOutMap>
                  )}
                </Flex>
              </TileContainer>

              {isAerovectUser && (
                <FlexWithGap>
                  <TileContainer>
                    <MultiViewTable title="Metrics" views={metricViews} />
                  </TileContainer>
                </FlexWithGap>
              )}
            </Content>
          </>
        )}
        <Footer />
      </Right>
    </FullPageContainer>
  );
};

const PageHeaderContainer = styled.div`
  font-size: 40px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  padding: 20px 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${(props) => props.theme.colors.paper};
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  gap: 10px;

  @media (max-width: 600px) {
    font-size: 32px;
    gap: 5px;
  }
`;

const Breadcrumb = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${(props) => props.theme.colors.grey};
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Chevron = styled(ChevronRight)`
  color: ${(props) => props.theme.colors.grey};
`;

const StatusHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  font-size: 24px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  padding: 20px;
`;

const StatusRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  padding: 20px 10px;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusName = styled.div`
  font-size: 16px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  margin-right: 20px;
`;

const StatusValue = styled.div`
  font-size: 16px;
  font-weight: ${(props) => props.theme.fontWeight.regular};
`;

const TldPngContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const BigTldPng = styled.img`
  max-width: 100px;
  width: 100%;
  height: auto; /* Keeps the aspect ratio intact */
`;

// Metrics wrapper. $standalone drops the reserved 60vh + divider; $grow fills
// the row's allocated vertical space in Content's wrap-stretch layout so the
// metrics TileContainer isn't taller than its contents with empty space inside.
const FlexWithGap = styled(Flex).attrs({ $standalone: true, $grow: true })`
  gap: 40px;
`;

export default VehicleDetail;
